import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Link } from '@resy/ui-kitchen';
import {
  getRetinaImage,
  handleMktAnalytics,
} from '../AmexUtils';
import './AmexNaaMgmComponent.scss';
import { ACQUISITION_CTA_EVENT_NAME } from '../constants';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';

const AmexNaaMgmComponent = (
  {
    awsFolderName,
    baseRequestUrl,
    matchMedia,
    sectionConfig: {
      badge,
      title,
      intro,
      copy,
      fine_print: finePrint,
      card_image: cardImage = null,
      image: {
        desktop: desktopImg = {},
        mobile: mobileImg = {},
      } = {},
      cta = {},
      section_id: sectionId,
      logo,
      logo_alt: logoAlt = '',
    },
    eventAnalytics,
    isNAA,
  }
) => {
  const urlPrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const imgName = getRetinaImage(desktopImg, mobileImg, matchMedia.lg);
  const imgCardName = cardImage ? `${urlPrefix}${getRetinaImage(cardImage?.desktop, cardImage?.mobile, matchMedia.lg)}` : null;
  const logoImage = `${urlPrefix}${logo}`;
  const image = `${urlPrefix}${imgName}`;

  const displayCTA = () => {
    const { label, url } = cta;

    if (Object.keys(cta).length === 0 || !label || !url) return null;
    return (
      <Link
        href={url}
        className="AmexNaaMgmComponent__cta"
        onClick={() => {
          handleMktAnalytics(eventAnalytics, ACQUISITION_CTA_EVENT_NAME, {
            action_name: isNAA ? 'NAA - Click' : 'MGM - Click',
          });
        }}
        target="_blank"
      >
        {label}
      </Link>
    );
  };

  const sanitizedBadge = sanitizeString(badge);
  const sanitizedTitle = sanitizeString(title);
  const sanitizedIntro = sanitizeString(intro);
  const sanitizedCopy = sanitizeString(copy);
  const sanitizedFinePrint = sanitizeString(finePrint);

  return (
    <section
      className={cx('AmexNaaMgmComponent', [
        isNAA ? 'naa' : 'mgm',
      ])}
    >
      <div className="container">
        <div className="AmexNaaMgmComponent__wrapper">
          <div className="AmexNaaMgmComponent__content">
            <div className="AmexNaaMgmComponent__content--inner">
              {logo && (
                <img className="AmexNaaMgmComponent__logo" src={logoImage} alt={logoAlt} />
              )}
              {(cardImage && matchMedia.lg) && (
                <img className="AmexNaaMgmComponent__cardImage" src={imgCardName} alt="" />
              )}
              {badge && (
                <p
                  className="AmexNaaMgmComponent__badge"
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedBadge,
                  }}
                />
              )}
              {title && (
                <h2
                  id={sectionId}
                  className="AmexNaaMgmComponent__title"
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedTitle,
                  }}
                />
              )}
              {sanitizedIntro && (
                <h3
                  className="AmexNaaMgmComponent__intro"
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedIntro,
                  }}
                />
              )}
              {copy && (
                <p
                  className="AmexNaaMgmComponent__copy"
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedCopy,
                  }}
                />
              )}

              {(cardImage && !matchMedia.lg) && (
              <img className="AmexNaaMgmComponent__cardImage" src={imgCardName} alt="" />
              )}

              {displayCTA()}
              {finePrint && (
                <p
                  className="AmexNaaMgmComponent__fine-print"
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedFinePrint,
                  }}
                />
              )}
              {(imgName && !matchMedia.lg) && (
                <img className="AmexNaaMgmComponent__img--mobile" src={image} alt="" />
              )}
            </div>
          </div>
          {(imgName && matchMedia.lg) && (
            <div
              className="AmexNaaMgmComponent__image"
              style={{
                backgroundImage: `url(${image})`,
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
};

AmexNaaMgmComponent.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  sectionConfig: PropTypes.shape({
    image: PropTypes.shape({
      desktop: PropTypes.shape({}).isRequired,
      mobile: PropTypes.shape({}).isRequired,
    }).isRequired,
    badge: PropTypes.string,
    intro: PropTypes.string,
    title: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    logo_alt: PropTypes.string,
    copy: PropTypes.string.isRequired,
    fine_print: PropTypes.string.isRequired,
    section_id: PropTypes.string.isRequired,
    item_name: PropTypes.string,
    card_image: PropTypes.shape({
      desktop: PropTypes.shape({}).isRequired,
      mobile: PropTypes.shape({}).isRequired,
    }),
    cta: PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      event_tag: PropTypes.string.isRequired,
      action_name: PropTypes.string,
      item_name: PropTypes.string,
    }).isRequired,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  isNAA: PropTypes.bool,
  eventAnalytics: PropTypes.func,
};

AmexNaaMgmComponent.defaultProps = {
  isNAA: false,
  eventAnalytics: null,
};

export default AmexNaaMgmComponent;
